// The api origin the SPA calls. Same-site with the app in staging/prod (api.* / app.* under one
// parent), so the httpOnly session cookie rides along on credentialed requests. Local default is
// the docker-compose api port (3100 → 3000 inside the container).
export const apiBaseUrl: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3100';

// The MCP server origin, shown in the "connect" snippet (ADR-0030). Explicit via VITE_MCP_BASE_URL,
// else derived from the api host (api.<env>.ggpathos.com → mcp.<env>.ggpathos.com).
export const mcpBaseUrl: string =
  (import.meta.env.VITE_MCP_BASE_URL as string | undefined) ??
  apiBaseUrl.replace('://api.', '://mcp.');

/**
 * The HUB's origin — the front door a shared link should point at (canon R9's `share` verb).
 *
 * ⚠️ NOT `window.location.origin`. `ProjectDocuments` renders under BOTH bundles — the Hub and the
 * analyzer — so reading the current origin mints a share link on whichever one the SHARER happened
 * to be in. A link built on `app.*` sends a colleague to the analyzer bundle, and `HubApp.tsx` says
 * why that is wrong in capitals: R9 is a latency budget with a number, and "a superintendent opening
 * a drawing on a phone in a trailer must not first download the analyzer's IRR heatmaps". A share
 * link is the one URL that reliably arrives on a phone.
 *
 * Derived from the api host exactly as `mcpBaseUrl` is (api.<env> -> hub.<env>), overridable for
 * local work where there is no hub host at all.
 */
export const hubBaseUrl: string =
  (import.meta.env.VITE_HUB_BASE_URL as string | undefined) ??
  apiBaseUrl.replace('://api.', '://hub.');

/** The override if set, else the api host's `api.` swapped for `app.`, else null (see below). */
export function deriveAppBaseUrl(override: string | undefined, api: string): string | null {
  if (override) return override;
  const derived = api.replace('://api.', '://app.');
  return derived === api ? null : derived;
}

/**
 * The ANALYZER's origin — where a run's report lives (`/analyses/:id`) and where a project's deals are
 * listed (`/analyses?project=`).
 *
 * ⚠️ THE HUB HAS NEITHER ROUTE, ON PURPOSE. `HubApp.tsx` does not import the report surface (R9's
 * latency budget), and its catch-all sends every unknown path to `/projects`. So a shared page that
 * navigates IN-APP to `/analyses/...` while rendered on the Hub does not fail — it silently lands the
 * reader on the project list. That was the "screen flipped" after a re-underwrite (#1396) and the
 * dead "See this project's deals" link (#1393). From the Hub those destinations are a CROSS-ORIGIN
 * link to this origin; from the analyzer they stay in-app (`AnalyzerLink`).
 *
 * Derived from the api host as `hubBaseUrl` is (api.<env> -> app.<env>), overridable for local work
 * where the two bundles run on different ports of one host.
 *
 * ⚠️ NULL WHEN IT CANNOT BE DERIVED — unlike `hubBaseUrl`, which falls back to the api origin itself.
 * With no `api.` in the host (local `http://localhost:3100`) the swap changes nothing, and a link built
 * on that would send a reader to the API server's JSON 404 dressed as a report link. Null makes the
 * caller decide; `AnalyzerLink` then renders no link at all rather than a wrong one.
 */
export const appBaseUrl: string | null = deriveAppBaseUrl(
  import.meta.env.VITE_APP_BASE_URL as string | undefined,
  apiBaseUrl,
);
